import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { API } from 'src/common/config/API';
import {
  isLocalSeoUser,
  logoutFromApp,
  sendInterComEvent
} from 'src/common/config/FunctionHelper';
import { removeLocalSeoAllStorage } from 'src/common/config/FunctionHelperPartTwo';
import { getSubscriptionDetailsStripe } from 'src/common/Scraper';
import { paths, stripeStatusTrial } from 'src/common/config/TextMessage';
import { RootState } from 'src/redux/store';
import { siteAuditCleanupInitialState } from 'src/redux/siteAuditReducer';
import { cleanupInitialState } from 'src/redux/counterSlice';
import {
  setStateStripeSubscription,
  userCleanupInitialState
} from 'src/redux/userReducer';
import { cleanupLocalSeoInitialState } from 'src/redux/localSeo';
import {
  getAccountStatus,
  getFeedbackOptions
} from '../utils/deactivateHelpers';
import { useUserDetails } from 'src/hooks/apiHooks/useUserDetails';

export const useDeactivateLogic = (
  handCloseModal: () => void,
  isCancel: boolean
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );
  const subscription: any = useSelector(
    (state: RootState) => state.user.subscription
  );
  const activePlanName = useSelector(
    (state: RootState) => state.user.activePlan
  );
  const planDetails = useSelector((state: RootState) => state.user.planDetails);
  const isPremiumUser = useSelector(
    (state: RootState) => state.user.premiumUser
  );
  const userEmail = useSelector((state: RootState) => state.counter.userEmail);
  const stripeLoader =
    useSelector((state: RootState) => state.siteAudit.stripeLoader) ?? true;
  const loader =
    useSelector((state: RootState) => state.siteAudit.loader) ?? true;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isNoOptionSelected, setIsNoOptionSelected] = useState<boolean>(false);
  const [otherReason, setOtherReason] = useState('');
  const [missingFeature, setMissingFeature] = useState('');
  const [isOtherFieldError, setIsOtherFieldError] = useState(null);
  const [isMissingFieldError, setIsMissingFieldError] = useState(null);
  const [openDowngradeOfferModal, setOpenDowngradeOfferModal] = useState(null);
  const [isMinCharOtherError, setIsMinCharOtherError] = useState(null);
  const [isMinCharMissingError, setIsMinCharMissingError] = useState(null);
  const [isOfferApplied, setIsOfferApplied] = useState(
    userDetails?.isOfferApplied
  );

  const { watch } = useForm({ defaultValues: { deactiveFeedback: '' } });
  const { refetch: refetchUserDetails } = useUserDetails({ enabled: false });
  const isFreePlan = activePlanName === planDetails?.free;
  const isLocalSeo = isLocalSeoUser();

  const { isNoPlanOrExpire } = getAccountStatus({
    stripeLoader,
    loader,
    reduxUserDetails: userDetails,
    userDetails,
    subscription,
    isPremiumUser,
    isFreePlan
  });

  const selectedTimeLine =
    subscription?.interval === 'month' ? 'Monthly' : 'Annually';
  const feedbackOptions = getFeedbackOptions({
    isOfferApplied,
    isFreePlan,
    isNoPlanOrExpire,
    isMember: userDetails?.role_type === 'member',
    selectedTimeLine
  });

  useEffect(() => {
    setIsOfferApplied(userDetails?.isOfferApplied);
  }, [userDetails]);

  const logout = async () => {
    await logoutFromApp();
    setTimeout(() => {
      setIsSuccessfullySubmitted(false);
      removeLocalSeoAllStorage();
      dispatch(cleanupInitialState());
      dispatch(userCleanupInitialState());
      dispatch(siteAuditCleanupInitialState());
      dispatch(cleanupLocalSeoInitialState());
      navigate(paths?.signin);
    }, 1000);
  };

  const handleSubmits = () => {
    if (isLocalSeo) {
      handleCancelSubscription();
      return;
    }
    setLoading(true);
    let userFeedback =
      selectedOption === 'Other'
        ? otherReason
        : selectedOption === 'Missing Features'
        ? missingFeature
        : selectedOption;
    API.userUpdate(
      API.EndPoint.userUpdate,
      API.Method.patch,
      undefined,
      {
        user_id: userDetails['userId'],
        request: { isDeactivated: 'true', email: userDetails?.['email'] },
        deactivationFeedback: userFeedback
      },
      (response) => {
        const responsData = { ...response?.data };
        if (responsData?.status === 'success') {
          if (subscription) {
            sendInterComEvent({
              cancel_during_trial: subscription.status === stripeStatusTrial,
              cancel_paid_plan: subscription.status !== stripeStatusTrial,
              is_stripe_customer: false
            });
          }
          setSuccessMessage(responsData?.message);
          setIsSuccessfullySubmitted(true);
          logout();
        } else {
          setError(responsData?.message);
          setTimeout(() => {
            setError('');
            setLoading(false);
          }, 2000);
        }
      }
    );
  };

  const handleCancelSubscription = () => {
    setLoading(true);
    let userFeedback =
      selectedOption === 'Other'
        ? otherReason
        : selectedOption === 'Missing Features'
        ? missingFeature
        : selectedOption;
    API.cancelSubscription(
      API.EndPoint.cancelSubscription,
      API.Method.post,
      undefined,
      {
        email: userEmail,
        subscription_id: subscription['subscription_id'],
        feedbackMessage: userFeedback
      },
      async (response) => {
        if (response?.data?.status === 'success') {
          setSuccessMessage(response?.data?.message);
          setIsSuccessfullySubmitted(true);
          const subDetails: any = await getSubscriptionDetailsStripe();
          dispatch(setStateStripeSubscription(subDetails || {}));
          await refetchUserDetails();
          setTimeout(() => {
            handCloseModal();
            setLoading(false);
          }, 2000);
        } else {
          setError(response?.data?.message);
          setTimeout(() => {
            setError('');
            setLoading(false);
          }, 2000);
        }
      }
    );
  };

  const handleToggle = (option: string) => {
    setSelectedOption((prev) => {
      const newSelection = prev === option ? null : option;
      if (isNoOptionSelected) setIsNoOptionSelected(newSelection === null);
      if (newSelection !== 'Other') {
        setOtherReason('');
        setIsOtherFieldError(false);
        setIsMinCharOtherError(false);
      }
      if (newSelection !== 'Missing Features') {
        setMissingFeature('');
        setIsMissingFieldError(false);
        setIsMinCharMissingError(false);
      }
      return newSelection;
    });
  };

  const handleContinue = () => {
    if (!selectedOption) {
      setIsNoOptionSelected(true);
      return;
    }
    if (selectedOption === 'Other' && otherReason?.length === 0) {
      setIsOtherFieldError(true);
      return;
    }
    if (selectedOption === 'Missing Features' && missingFeature?.length === 0) {
      setIsMissingFieldError(true);
      return;
    }
    if (selectedOption === 'Other' && otherReason?.length < 30) {
      setIsMinCharOtherError(true);
      return;
    }
    if (selectedOption === 'Missing Features' && missingFeature?.length < 30) {
      setIsMinCharMissingError(true);
      return;
    }
    if (selectedOption === 'Other' || selectedOption === 'Missing Features') {
      handleSubmits();
      return;
    }
    setIsMinCharOtherError(false);
    setIsMinCharMissingError(false);
    setIsNoOptionSelected(false);
    setIsOtherFieldError(false);
    setIsMissingFieldError(false);
    setOpenDowngradeOfferModal(true);
  };

  return {
    loading,
    error,
    successMessage,
    isSuccessfullySubmitted,
    selectedOption,
    isNoOptionSelected,
    otherReason,
    missingFeature,
    isOtherFieldError,
    isMissingFieldError,
    openDowngradeOfferModal,
    isMinCharOtherError,
    isMinCharMissingError,
    feedbackOptions,
    isLocalSeo,
    activePlanName,
    selectedTimeLine,
    subscription,
    setOtherReason,
    setMissingFeature,
    setIsOtherFieldError,
    setIsMissingFieldError,
    setOpenDowngradeOfferModal,
    handleToggle,
    handleContinue,
    handleSubmits
  };
};
